import { firstValueFrom } from "rxjs";

import { ChangeDetectionStrategy, Component, computed, effect, inject, resource, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";

import { WirelessOrganisationTicketsGQL } from "../../apis/networkdashboard/graphql/graphql";
import { FooterComponent } from "../../components/common/footer/footer";
import { NavigationComponent } from "../../components/common/navigation/navigation";
import { DialogWirelessTicketsComponent } from "../../components/dialogs/dialog-wireless-tickets/dialog-wireless-tickets";
import { EMPTY_HEALTH } from "../../components/models/HealthIndicator";
import { WirelessTicket } from "../../components/models/wireless-ticket";
import { collectTags } from "../../helpers/subscription/collect-tags";
import { AuthService } from "../../services/authservice";
import { SubscriptionListService } from "../../services/subscription-list.service";
import { getFilteredItems } from "./helpers/get-filtered-items";
import { WirelessSubscriptionsFilterComponent } from "./wireless-subscriptions-filter";
import { WirelessSubscriptionsResultsComponent } from "./wireless-subscriptions-results";

@Component({
  selector: "page-wireless-subscriptions",
  templateUrl: "wireless-subscriptions.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FooterComponent,
    NavigationComponent,
    WirelessSubscriptionsFilterComponent,
    WirelessSubscriptionsResultsComponent,
  ],
})
export class WirelessSubscriptionsPage {
  private auth = inject(AuthService);
  private subscriptionListService = inject(SubscriptionListService);
  private wirelessTicketsGQL = inject(WirelessOrganisationTicketsGQL);
  private dialog = inject(MatDialog);
  private router = inject(Router);

  protected tickets = computed(() => this.organisationTickets.value());

  // Opened via the "view all tickets" link in a location dialog: land here and open the org dialog
  // once the tickets have loaded. Carried in router navigation state (not the URL) so it doesn't
  // affect the route title or persist on refresh.
  private openTicketsDialogOnArrival = this.router.getCurrentNavigation()?.extras.state?.["openTickets"] === true;
  private ticketsDialogOpened = false;

  constructor() {
    effect(() => {
      if (this.openTicketsDialogOnArrival && !this.organisationTickets.isLoading() && !this.ticketsDialogOpened) {
        this.ticketsDialogOpened = true;
        this.dialog.open(DialogWirelessTicketsComponent, {
          data: { tickets: this.tickets(), scope: "organisation" },
        });
      }
    });
  }

  private organisationTickets = resource<WirelessTicket[], { accessCustomerId: string } | undefined>({
    params: () => {
      const customerId = this.auth.viewingCustomer().customerId;
      return customerId ? { accessCustomerId: customerId } : undefined;
    },
    loader: ({ params }) =>
      firstValueFrom(this.wirelessTicketsGQL.fetch({ variables: params }))
        .then(({ data }) => data?.wirelessOrganisationTickets ?? [])
        .catch((reason) => {
          console.warn("Failed to retrieve organisation tickets:", reason);
          return [];
        }),
    defaultValue: [],
  });

  private userLoaded = toSignal(this.auth.userLoaded, { initialValue: false });
  private _subscriptions = resource({
    params: () => ({ loaded: this.userLoaded() }),
    loader: ({ params }) =>
      params.loaded ? firstValueFrom(this.subscriptionListService.fetchWireless()) : Promise.resolve([]),
    defaultValue: [],
  });

  private subscriptions = computed(() => this._subscriptions.value() ?? []);
  protected activeTags = signal<string[]>([]);
  protected servicesSearch = signal<string>("");
  protected collectedTags = computed(() => collectTags(this.subscriptions()));
  protected suppliers = computed(() => [...new Set(this.subscriptions().map((s) => s.supplier))]);
  protected filteredSubscriptions = computed(() =>
    getFilteredItems(
      this.subscriptions().map((subscription) => ({ subscription, disruption: undefined, health: EMPTY_HEALTH })),
      [],
      this.activeTags(),
      [],
      this.servicesSearch(),
    ).map(({ subscription }) => subscription),
  );
}
